import {
  FORMAT,
  RESOURCE_TYPE_STAFFER,
  RESOURCE_TYPE_APPLIANCE,
  APPOINTMENT_STATUSES_MAP,
  BOOKING_TYPE_BUSINESS,
  BOOKING_TYPE_CUSTOMER,
  BOOKING_TYPE_RESERVATION,
  BOOKING_TYPE_TIMEOFF,
  BOOKING_TYPE_BREAK,
  PARTNER_APP_TYPES,
  PARTNER_APP_DATA_SYNC_STATUS,
} from '@/lib/consts.ts'

import { shapeFromEvent } from '@/components/calendar/functions'

import moment from 'moment'

import Proxy from '@/lib/object-proxy.ts'

type EventCustomer = {
  id?: string | number
  name?: string
}

type EventResource = {
  type?: string
  is_current_user?: boolean
}

type EventMeta = {
  customer?: EventCustomer
  status?: string
  type?: string
  paid?: boolean
  from_promo?: boolean
  new?: boolean
}

type EventProxyHost = {
  booked_from?: string
  booked_till?: string
  gap_hole_start?: string
  gap_hole_end?: string
  customer?: EventCustomer
  $meta?: EventMeta
  status?: string
  type?: string
  resources?: EventResource[]
  paid?: boolean
  from_promo?: boolean
  partner_app_data?: Record<string, Record<string, string>>
  isBooking?: boolean
  gapStart?: moment.Moment | null | false
  gapEnd?: moment.Moment | null | false
  _type?: string
  _status?: string
}

export default Proxy({

  from: function (this: EventProxyHost) {
    return moment(this.booked_from, FORMAT)
  },

  till: function (this: EventProxyHost) {
    return moment(this.booked_till, FORMAT)
  },

  gapStart: function (this: EventProxyHost) {
    return this.gap_hole_start && moment(this.gap_hole_start, FORMAT)
  },

  gapEnd: function (this: EventProxyHost) {
    return this.gap_hole_end && moment(this.gap_hole_end, FORMAT)
  },

  shape: function (this: EventProxyHost) {
    return shapeFromEvent(this)
  },

  customerName: function (this: EventProxyHost) {
    if (this.customer && (this.customer.id || this.customer.name)) {
      return this.customer.name || ''
    }

    if (this.$meta && this.$meta.customer && (this.$meta.customer.id || this.$meta.customer.name)) {
      return this.$meta.customer.name || ''
    }

    return ''
  },

  isWalkin: function (this: EventProxyHost) {
    return !(this.customer && this.customer.id)
  },

  _status: function (this: EventProxyHost) {
    return this.status || (this.$meta && this.$meta.status)
  },

  _type: function (this: EventProxyHost) {
    return this.type || (this.$meta && this.$meta.type)
  },

  staffer: function (this: EventProxyHost) {
    if (!this.resources) return null
    return this.resources.find(r => r.type === RESOURCE_TYPE_STAFFER)
  },

  appliance: function (this: EventProxyHost) {
    if (!this.resources) return null
    return this.resources.find(r => r.type === RESOURCE_TYPE_APPLIANCE)
  },

  isBooking: function (this: EventProxyHost) {
    return this._type === BOOKING_TYPE_BUSINESS || this._type === BOOKING_TYPE_CUSTOMER
  },

  hasGapHole: function (this: EventProxyHost) {
    return this.isBooking && !!this.gapStart && !!this.gapEnd
  },

  isPaid: function (this: EventProxyHost) {
    return this.$meta ? this.$meta.paid : this.paid
  },

  isTimeoff: function (this: EventProxyHost) {
    return this._type === BOOKING_TYPE_TIMEOFF
  },

  isBreak: function (this: EventProxyHost) {
    return this._type === BOOKING_TYPE_BREAK
  },

  isNoShow: function (this: EventProxyHost) {
    return this._status === APPOINTMENT_STATUSES_MAP.NOSHOW
  },

  isCanceled: function (this: EventProxyHost) {
    return this._status === APPOINTMENT_STATUSES_MAP.CANCELED
  },

  isReservation: function (this: EventProxyHost) {
    return this._type === BOOKING_TYPE_RESERVATION
  },

  isNonworking: function (this: EventProxyHost) {
    return false
  },

  isBoost: function (this: EventProxyHost) {
    return this.from_promo || (this.$meta && this.$meta.from_promo)
  },

  needsConfirmation: function (this: EventProxyHost) {
    // modified or waiting
    return this._status === APPOINTMENT_STATUSES_MAP.MODIFIED
      || this._status === APPOINTMENT_STATUSES_MAP.UNCONFIRMED
  },

  isNew: function (this: EventProxyHost) {
    return this.$meta && this.$meta.new
  },

  isPhysioSyncPending: function (this: EventProxyHost) {
    return this.partner_app_data?.[PARTNER_APP_TYPES.PHYSIOREGISTRATION]?.['sync_status'] === PARTNER_APP_DATA_SYNC_STATUS.PENDING
  },

  isCurrentUser: function (this: EventProxyHost) {
    return !!this.resources?.find(e => e.is_current_user)
  },

})
